import { Dialog } from '@headlessui/react';
import { X } from 'lucide-react';
import { AtivoForm } from './AtivoForm';
import type { AssetDetail, CreateAssetPayload } from '../api';
import { useCriarAtivo, useAtualizarAtivo } from '../hooks';
import { useState } from 'react';
import { Alert } from '../../../shared/components/Alert';

interface Props {
  open: boolean;
  /** Se ausente, é criação. Se presente, edição. */
  initial?: AssetDetail;
  onClose: () => void;
  /** Chamado após POST/PUT com sucesso — recebe o AssetDetail salvo. */
  onSuccess: (asset: AssetDetail) => void;
}

/**
 * Modal wrapper do AtivoForm. Encapsula useCriarAtivo/useAtualizarAtivo
 * e o Dialog do Headless UI. Após save chama onSuccess; caller decide o
 * que fazer (navegar pro detalhe, fechar, exibir mensagem).
 *
 * Largura max-w-3xl acomoda o grid de 2 colunas dos campos sem esticar
 * demais numa tela grande.
 */
export function AtivoFormModal({ open, initial, onClose, onSuccess }: Props) {
  const criar = useCriarAtivo();
  const atualizar = useAtualizarAtivo(initial?.asset.id ?? '');
  const [error, setError] = useState<string | null>(null);
  const isEdit = !!initial;

  async function handleSubmit(payload: CreateAssetPayload) {
    setError(null);
    try {
      const result = isEdit
        ? await atualizar.mutateAsync(payload)
        : await criar.mutateAsync(payload);
      onSuccess(result);
      onClose();
    } catch (err: unknown) {
      const data = (err as { response?: { data?: { message?: string | string[] } } }).response?.data;
      const msg = data?.message;
      setError(
        Array.isArray(msg) ? msg.join(', ')
          : typeof msg === 'string' ? msg
          : `Falha ao ${isEdit ? 'atualizar' : 'criar'} ativo.`,
      );
    }
  }

  return (
    <Dialog open={open} onClose={onClose} className="relative z-40">
      <div className="fixed inset-0 bg-black/40" aria-hidden="true" />
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <Dialog.Panel className="max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded-lg bg-white p-6 shadow-xl">
          <div className="mb-4 flex items-center justify-between">
            <Dialog.Title className="text-lg font-semibold text-gray-900">
              {isEdit ? 'Editar ativo' : 'Novo ativo'}
            </Dialog.Title>
            <button onClick={onClose} className="rounded p-1 text-gray-500 hover:bg-gray-100">
              <X className="h-5 w-5" />
            </button>
          </div>

          {error && <div className="mb-3"><Alert variant="error">{error}</Alert></div>}

          <AtivoForm
            initial={initial}
            onSubmit={handleSubmit}
            onCancel={onClose}
            submitting={criar.isPending || atualizar.isPending}
            submitLabel={isEdit ? 'Salvar alterações' : 'Cadastrar ativo'}
          />
        </Dialog.Panel>
      </div>
    </Dialog>
  );
}
